/**
 * PRP-WEB-1 W1.3 / W3.3: save floor plan edits from the web.
 *
 * Contract (lane web-1-api, `app/api/routes/project_plan.py`):
 *   PUT /api/v1/projects/{id}/plan
 *   body  { rooms: [{ room_id, geometry (RoomGeometryV2), base_geometry_version, base_updated_at }] }
 *         `geometry` REPLACES the stored room (as a phone save does). `base_updated_at` is the
 *         marker note's `updated_at` as loaded (the edit-race token); `base_geometry_version` is
 *         the stored schema version (the server writes max(stored, 2), never a counter).
 *   200   { rooms_saved, rooms: [{ room_id, updated_at, geometry_version }],
 *           estimates: [{ parse_hash, name, rooms_resized, lines_requantified,
 *           typed_quantity_lines: [{ item, room, code, quantity, unit }], batch_id?, error? }] }
 *   409   detail { code: "plan_changed", message, room_id? }
 *   422   detail { code: invalid_geometry | unknown_room | not_editable, message, room_id? }
 *   404   not your project, or WEB_PLAN_EDIT is off;  503 store_error
 * A list from day one so the PRP-WEB-2 drag editor posts the same shape. Same auth/fetch path
 * as api.ts; failures arrive as `ApiError` (`status`, `code`).
 *
 * PRP-WEB-2 (lane web-2-api) adds, all optional:
 *   PUT  body  + groups: [{ main_room_id, subroom_ids (ordered) }]
 *              + underlay: { path, scale_ft_per_px, offset: {x, y}, rotation, opacity } | null
 *              + deleted_room_ids: [room_id]       (rooms the web deleted)
 *              + base_project_updated_at           (the groups/underlay race token)
 *              rooms[] rows may carry create: true (a new room: client uuid, base 0 / null)
 *        422  invalid_groups; room_has_lines { room_id } (a linked chat estimate has lines there)
 *   GET  /projects/{id}/plan/extras -> { groups, underlay: {url, scale_ft_per_px, offset, rotation, opacity} | null, project_updated_at }
 *   POST /projects/{id}/plan/underlay (multipart `file`) -> { path }
 */
import { api } from './api';
import type { RoomGeometryV2 } from '@sketch-core';

export interface PlanRoomEdit {
  room_id: string;
  geometry: RoomGeometryV2;
  base_geometry_version: number;
  base_updated_at: string | null;
  /** A room made on the web (split, drag-out, closet, paste, add): the server creates its marker. */
  create?: boolean;
}

/** One subroom group: a main room and its subrooms IN ORDER (F1, PF2 address them by position). */
export interface PlanGroup { main_room_id: string; subroom_ids: string[] }

/** The picture under the plan (DD5: the owner's own upload, never public). */
export interface PlanUnderlay {
  path: string;
  scale_ft_per_px: number;
  offset: { x: number; y: number };
  rotation: number;
  opacity: number;
}

export interface PlanExtras {
  groups: PlanGroup[];
  underlay: (Omit<PlanUnderlay, 'path'> & { url: string; path?: string }) | null;
  project_updated_at: string | null;
}

/** Everything a save sends besides the room rows (each key only when it changed). */
export interface PlanSaveExtras {
  groups?: PlanGroup[];
  underlay?: PlanUnderlay | null;
  deleted_room_ids?: string[];
  base_project_updated_at?: string | null;
}

/** A typed-quantity line kept as it was (its quantity was typed, not a calc). */
export interface TypedQuantityLine { item?: string | null; room?: string | null; code?: string | null; quantity?: number | null; unit?: string | null }

/** One chat estimate that followed the plan (only `mcp-` estimates linked to the project, DD4). */
export interface PlanSaveEstimate {
  parse_hash: string;
  name?: string | null;
  /** A count or the resized rooms' names; both are accepted. */
  rooms_resized?: number | string[] | null;
  lines_requantified?: number | null;
  /** Lines whose quantity was typed (not a calc), kept as they were. */
  typed_quantity_lines?: (TypedQuantityLine | string)[] | null;
  batch_id?: string | null;
  /** The estimate did not follow (the next chat call re-syncs it); the plan itself was saved. */
  error?: string | null;
}

export interface PlanSaveResult {
  rooms_saved: number | string[];
  /** The saved rooms' new bases for the next save. */
  rooms?: { room_id: string; updated_at?: string | null; geometry_version?: number | null }[];
  /** The rooms this save deleted (PRP-WEB-2). */
  deleted_room_ids?: string[];
  estimates: PlanSaveEstimate[];
}

export const PLAN_CHANGED = 'plan_changed';

/** A refused save (422) with the room it names, so the page can select that room. */
export class PlanSaveError extends Error {
  constructor(message: string, public status: number, public code?: string, public roomId?: string) { super(message); }
}

const base = (projectId: string) => `/projects/${encodeURIComponent(projectId)}/plan`;

export const planApi = {
  /**
   * One save: the changed rooms plus whatever extras changed. A 422 comes back as a
   * `PlanSaveError` with its `room_id` (the shared error path drops it); a 409 stays an `ApiError`.
   */
  savePlan: async (projectId: string, rooms: PlanRoomEdit[], extras: PlanSaveExtras = {}): Promise<PlanSaveResult> => {
    const out = await api.put(base(projectId), { rooms, ...extras }, {
      validateStatus: (s) => (s >= 200 && s < 300) || s === 422,
    }) as unknown as PlanSaveResult & { detail?: unknown };
    const d = out?.detail as { code?: string; message?: string; room_id?: string } | string | undefined;
    if (d !== undefined) {
      const o = typeof d === 'object' && d ? d : { message: String(d) };
      throw new PlanSaveError(o.message || 'The floor plan could not be saved.', 422, o.code, o.room_id);
    }
    return out;
  },
  /** Groups and the underlay; an older server without the route answers 404, read as "none". */
  getExtras: async (projectId: string): Promise<PlanExtras> => {
    try {
      const x = await api.get(`${base(projectId)}/extras`) as unknown as Partial<PlanExtras>;
      return { groups: Array.isArray(x?.groups) ? x.groups : [], underlay: x?.underlay ?? null, project_updated_at: x?.project_updated_at ?? null };
    } catch (err) {
      if ((err as { status?: number })?.status === 404) return { groups: [], underlay: null, project_updated_at: null };
      throw err;
    }
  },
  uploadUnderlay: (projectId: string, file: File): Promise<{ path: string }> => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`${base(projectId)}/underlay`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const ROOM_HAS_LINES = 'room_has_lines';

/** True when a save failed because the stored plan moved on (reload, then edit again). */
export function isPlanChanged(err: unknown): boolean {
  const e = err as { status?: number; code?: string } | null;
  return !!e && e.status === 409 && e.code === PLAN_CHANGED;
}
